import { ChangeDetectorRef, Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AsyncPipe, NgIf, NgFor } from '@angular/common';
import { select, Store } from '@ngrx/store';
import { Observable, tap } from 'rxjs';
import { PreloaderColor } from '@enums/shared.enum';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatTabsModule } from '@angular/material/tabs';
import { MatBadgeModule } from '@angular/material/badge';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { UiKitModule } from '../../shared/ui-kit/ui-kit.module';
import { AppState } from '../../store/app.state';
import { AlarmsSelectors } from '@states/alarm-mangment/alarm-management.selectros';
import { AlarmManagementModel } from '@models/alarm-management.model';
import { AlarmManagementAction } from '@states/alarm-mangment/alarm-management.action-types';
import { LocationSelectors } from '@states/location/location.selector-types';
import { UiUserSettingsDateComponent } from '../../shared/ui-kit/ui-user-settings-date/ui-user-settings-date.component';
import { UiPreloaderComponent } from '../../shared/ui-kit/ui-preloader/ui-preloader.component';
import { CameraSelectors } from '@states/camera/camera.selector-types';
import { AlertDetailsDialogComponent } from './alert-details-dialog/alert-details-dialog.component';
import { AttachmentViewerDialogComponent } from './attachment-viewer-dialog/attachment-viewer-dialog.component';
import { ImagePreviewDialogComponent } from './image-preview-dialog/image-preview-dialog.component';
import { AuthSrcDirective } from '../../shared/directives/auth-src.directive';
import { ThumbnailsService } from '../../development/thumbnails.service';
import { StorageBucket } from '@consts/storage-url.util';
import { UiUserSettingsDateType } from '@enums/shared.enum';
import { UiButtonComponent } from '@ui';

@Component({
  selector: 'app-alarms-management',
  standalone: true,
  imports: [NgIf, NgFor, AsyncPipe, UiKitModule, MatIconModule, MatTooltipModule, ScrollingModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatTabsModule, MatBadgeModule, MatExpansionModule, MatPaginatorModule, ReactiveFormsModule, TranslatePipe, UiUserSettingsDateComponent, UiPreloaderComponent, UiButtonComponent, AuthSrcDirective],
  templateUrl: './alarms-management.component.html',
  styleUrl: './alarms-management.component.scss',
})
export class AlarmsManagementComponent implements OnInit {
  private readonly failedThumbnailKeys = new Set<string>();

  @ViewChild('noActiveAlarmsContent') noActiveAlarmsContent!: TemplateRef<any>;
  @ViewChild('noCanceledAlarmsContent') noCanceledAlarmsContent!: TemplateRef<any>;

  public activeAlarms$: Observable<AlarmManagementModel.AlarmData[]> = this.store$.pipe(
    select(AlarmsSelectors.selectActiveAlarms),
    tap((data) => {}),
  );

  public canceledAlarms$: Observable<AlarmManagementModel.AlarmData[]> = this.store$.pipe(
    select(AlarmsSelectors.selectCanceledAlarms),
    tap((data) => {}),
  );

  // New observables for enhanced features
  public alarmsWithAlerts$: Observable<AlarmManagementModel.AlarmWithAlerts[]> = this.store$.pipe(
    select(AlarmsSelectors.selectAlarmsWithAlerts),
  );

  public rejectedAlerts$: Observable<AlarmManagementModel.RejectedAlert[]> = this.store$.pipe(
    select(AlarmsSelectors.selectRejectedAlerts),
  );

  public rejectedAlertsTotal$: Observable<number> = this.store$.pipe(
    select(AlarmsSelectors.selectRejectedAlertsTotal),
  );

  public rejectedAlertsPagination$ = this.store$.pipe(
    select(AlarmsSelectors.selectRejectedAlertsPagination),
  );

  public rejectedAlertsLoading$: Observable<boolean> = this.store$.pipe(
    select(AlarmsSelectors.selectRejectedAlertsLoading),
  );

  public selectedTab$: Observable<AlarmManagementModel.SelectedTab> = this.store$.pipe(
    select(AlarmsSelectors.selectSelectedTab),
  );

  public expandedAlarmIds$: Observable<string[]> = this.store$.pipe(
    select(AlarmsSelectors.selectExpandedAlarmIds),
  );

  public locationName$(locationId: string) {
    return this.store$.select(LocationSelectors.selectLocationName(locationId));
  }

  public cameraName$(cameraId: string) {
    return this.store$.select(CameraSelectors.selectCameraNameById(cameraId));
  }

  public alertInstances$(alarmId: string) {
    return this.store$.select(AlarmsSelectors.selectAlarmAlertInstances(alarmId));
  }

  public alertInstancesLoading$(alarmId: string) {
    return this.store$.select(AlarmsSelectors.selectAlertInstancesLoading(alarmId));
  }

  public noonlightActivities$(alertInstanceId: number) {
    return this.store$.select(AlarmsSelectors.selectNoonlightActivities(alertInstanceId));
  }

  public noonlightActivityLoading$(alertInstanceId: number) {
    return this.store$.select(AlarmsSelectors.selectNoonlightActivityLoading(alertInstanceId));
  }

  public isAlarmExpanded$(alarmId: string) {
    return this.store$.select(AlarmsSelectors.selectIsAlarmExpanded(alarmId));
  }

  public isLoading$: Observable<boolean> = this.store$.pipe(select(AlarmsSelectors.selectIsLoading));

  public activeAlarmsCount$: Observable<number> = this.store$.pipe(
    select(AlarmsSelectors.selectActiveAlarmsCount),
  );

  public loaderColor = PreloaderColor;
  public AlarmStatus = AlarmManagementModel.AlarmStatus;
  public UiUserSettingsDateType = UiUserSettingsDateType;

  public createAlarmForm: FormGroup;
  public selectedTab: AlarmManagementModel.SelectedTab = 'active';

  constructor(
    private store$: Store<AppState>,
    private dialog: MatDialog,
    private fb: FormBuilder,
    private thumbnailsService: ThumbnailsService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.createAlarmForm = this.fb.group({
      locationId: [''],
      uniformId: [''],
    });
  }

  ngOnInit() {
    this.loadAlarms();
    this.selectedTab$.subscribe(tab => {
      this.selectedTab = tab;
    });
  }

  loadAlarms() {
    this.store$.dispatch(AlarmManagementAction.startGetActiveAlarms());
    this.store$.dispatch(AlarmManagementAction.startGetCanceledAlarms());
    this.store$.dispatch(AlarmManagementAction.startGetAlarmsWithAlerts({ filter: { status: AlarmManagementModel.AlarmStatus.ACTIVE } }));
  }

  loadRejectedAlerts(pagination?: { limit?: number; offset?: number }) {
    this.store$.dispatch(AlarmManagementAction.startGetRejectedAlerts({
      filter: pagination || {}
    }));
  }

  onRejectedAlertsPageChange(event: PageEvent) {
    const offset = event.pageIndex * event.pageSize;
    this.loadRejectedAlerts({ limit: event.pageSize, offset });
  }

  onTabChange(tabIndex: number) {
    const tabs: AlarmManagementModel.SelectedTab[] = ['active', 'handled', 'rejected'];
    const selectedTab = tabs[tabIndex];
    this.store$.dispatch(AlarmManagementAction.setSelectedTab({ tab: selectedTab }));

    if (selectedTab === 'rejected') {
      this.loadRejectedAlerts();
    } else if (selectedTab === 'active') {
      this.store$.dispatch(AlarmManagementAction.startGetAlarmsWithAlerts({ filter: { status: AlarmManagementModel.AlarmStatus.ACTIVE } }));
    } else if (selectedTab === 'handled') {
      this.store$.dispatch(AlarmManagementAction.startGetAlarmsWithAlerts({ filter: { status: AlarmManagementModel.AlarmStatus.CANCELED } }));
    }
  }

  onCreateAlarm() {
    if (this.createAlarmForm.valid) {
      const createAlarmData: AlarmManagementModel.CreateAlarm = {
        locationId: this.createAlarmForm.value.locationId,
        uniformId: this.createAlarmForm.value.uniformId || undefined,
      };

      this.store$.dispatch(AlarmManagementAction.createAlarm({ createAlarm: createAlarmData }));
      this.createAlarmForm.reset();
    }
  }

  cancelAlarmById(alarmId: string) {
    this.store$.dispatch(AlarmManagementAction.cancelAlarm({ alarmId }));
    // Refresh the alarms list after a short delay to allow the API to process
    setTimeout(() => {
      this.store$.dispatch(AlarmManagementAction.startGetAlarmsWithAlerts({ filter: { status: AlarmManagementModel.AlarmStatus.ACTIVE } }));
    }, 1000);
  }

  refresh() {
    this.loadAlarms();
    if (this.selectedTab === 'rejected') {
      this.loadRejectedAlerts();
    }
  }

  toggleAlarmExpansion(alarmId: string) {
    this.store$.dispatch(AlarmManagementAction.toggleExpandedAlarm({ alarmId }));
    // Load alert instances when expanding
    this.isAlarmExpanded$(alarmId).subscribe(isExpanded => {
      if (isExpanded) {
        this.store$.dispatch(AlarmManagementAction.getAlarmAlertInstances({ alarmId }));
      }
    }).unsubscribe();
  }

  loadNoonlightActivity(alertInstanceId: number) {
    this.store$.dispatch(AlarmManagementAction.getAlertNoonlightActivity({ alertInstanceId }));
  }

  openAlertDetailsDialog(alert: AlarmManagementModel.AlertInstanceSummary) {
    // Map AlertInstanceSummary to RejectedAlert format for the dialog
    const alertData: AlarmManagementModel.RejectedAlert = {
      alertInstanceId: alert.alertInstanceId,
      locationId: '',
      cameraId: alert.cameraId,
      eventId: alert.eventId,
      timestamp: alert.timestamp,
      mainThumbnail: alert.mainThumbnail,
    };

    this.dialog.open(AlertDetailsDialogComponent, {
      data: { alert: alertData } as AlarmManagementModel.AlertDetailsDialogData,
      panelClass: 'alert-details-dialog-panel',
      autoFocus: false,
    });
  }

  getStatusText(status: AlarmManagementModel.AlarmStatus): string {
    return status === AlarmManagementModel.AlarmStatus.ACTIVE ? 'Active' : 'Handled';
  }

  getStatusClass(status: AlarmManagementModel.AlarmStatus): string {
    return status === AlarmManagementModel.AlarmStatus.ACTIVE ? 'status-active' : 'status-handled';
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleString();
  }

  getTimeDuration(startTime: string, endTime?: string): string {
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const durationMs = end.getTime() - start.getTime();

    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  }

  getOperationTypeLabel(operationType: string): string {
    const labels: { [key: string]: string } = {
      'verification_request': 'Verification Request',
      'verification_response': 'Verification Response',
      'dispatch_request': 'Dispatch Request',
      'dispatch_response': 'Dispatch Response',
    };
    return labels[operationType] || operationType;
  }

  getOperationTypeClass(operationType: string): string {
    if (operationType.includes('request')) {
      return 'operation-request';
    }
    return 'operation-response';
  }

  trackByAlarmId(index: number, alarm: AlarmManagementModel.AlarmWithAlerts): string {
    return alarm.id;
  }

  trackByAlertInstanceId(index: number, alert: AlarmManagementModel.AlertInstanceSummary): number {
    return alert.alertInstanceId;
  }

  trackByActivityIndex(index: number, activity: AlarmManagementModel.NoonlightActivityLog): number {
    return activity.requestTimestamp;
  }

  onRejectedAlertClick(alert: AlarmManagementModel.RejectedAlert) {
    this.dialog.open(AlertDetailsDialogComponent, {
      data: { alert } as AlarmManagementModel.AlertDetailsDialogData,
      panelClass: 'alert-details-dialog-panel',
      autoFocus: false,
    });
  }

  openAttachment(url: string) {
    this.dialog.open(AttachmentViewerDialogComponent, {
      data: { url },
      width: '700px',
      maxHeight: '80vh',
      autoFocus: false,
    });
  }

  getThumbUrl(path: string) {
    const baseUrl = this.thumbnailsService.getBucketBaseUrl(StorageBucket.Thumbnails, true);
    return `${baseUrl}/thumbnails/${path}`;
  }

  openImagePreview(thumbPath: string, event: MouseEvent) {
    event.stopPropagation();
    this.dialog.open(ImagePreviewDialogComponent, {
      data: { url: this.getThumbUrl(thumbPath) } as AlarmManagementModel.ImagePreviewDialogData,
      panelClass: 'image-preview-dialog-panel',
      maxWidth: '100vw',
      maxHeight: '100vh',
      autoFocus: false,
    });
  }

  public isAlertThumbFailed(alertInstanceId: number): boolean {
    return this.failedThumbnailKeys.has(`ai-${alertInstanceId}`);
  }

  public onAlertThumbError(alertInstanceId: number): void {
    this.failedThumbnailKeys.add(`ai-${alertInstanceId}`);
    this.cdr.detectChanges();
  }

  public isRejectedThumbFailed(alertInstanceId: string | number): boolean {
    return this.failedThumbnailKeys.has(`rj-${alertInstanceId}`);
  }

  public onRejectedThumbError(alertInstanceId: string | number): void {
    this.failedThumbnailKeys.add(`rj-${alertInstanceId}`);
    this.cdr.detectChanges();
  }
}
